import { StatusDisplay } from './StatusDisplay';
import { OtpInput } from './otp-input';
import IconsMapping from '~/components/ui/Icons/IconsMapping';

interface OtpVerificationProps {
  email: string;
  otp: string;
  onOtpChange: (value: string) => void;
  state: 'idle' | 'verifying' | 'verified' | 'failed';
  error: string;
  canResend: boolean;
  resendCountdown: number;
  onVerify: (otpValue?: string) => void;
  onResend: () => void;
  onBack: () => void;
}

// Email masking function
const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) {
    return email;
  }

  if (localPart.length <= 2) {
    return email;
  }

  const domainParts = domain.split('.');
  const subdomain = domainParts[1] || domainParts[0] || '';

  return `${localPart.substring(0, 2)}${'*'.repeat(6)}.${subdomain}`;
};

export default function OtpVerification({
  email,
  otp,
  onOtpChange,
  state,
  error,
  canResend,
  resendCountdown,
  onVerify,
  onResend,
  onBack,
}: OtpVerificationProps) {
  return (
    <div className="w-full max-w-md mx-auto font-inter-display px-4">
      <div className="flex flex-col gap-4 sm:gap-4">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-[#FDFDFD] text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-medium leading-[1.2em] mb-2">
            Verify your email
          </h1>

          <div className="text-[#696A71] text-base sm:text-md lg:text-md font-normal leading-[1.2em]">
            <p>We've sent a 6-digit code to {maskEmail(email)}</p>
            <p>Enter it below to continue.</p>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-5">
            {/* OTP Input Component */}
            <div className="flex justify-between py-4">
              <OtpInput
                value={otp}
                onChange={(value: string) => {
                  onOtpChange(value);
                }}
                maxLength={6}
                disabled={state === 'verifying'}
                onComplete={(value) => onVerify(value)}
              />
            </div>
          </div>

          {/* Status Messages */}
          <div className="space-y-3">
            {state === 'failed' && <StatusDisplay status="failed" message={error} title="Verification Failed" />}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => onVerify()}
              disabled={!otp || otp.length !== 6 || state === 'verifying'}
              className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors text-black min-h-[48px] sm:min-h-auto ${
                otp && otp.length === 6 && state !== 'verifying'
                  ? 'bg-[#F7F7F7] hover:bg-[#E5E5E5]'
                  : 'bg-[#CCCCCC] cursor-not-allowed'
              }`}
            >
              {state === 'verifying' ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-[#CCCCCC] rounded-full animate-spin"></div>
                  <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Verifying...</span>
                </div>
              ) : state === 'verified' ? (
                <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Verified</span>
              ) : (
                <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Continue</span>
              )}
            </button>

            {/* Bottom Navigation */}
            <div className="text-center text-[rgba(255,255,255,0.5)] text-sm font-normal leading-[1.2em] flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
              <button
                type="button"
                className="text-white flex items-center justify-center sm:justify-start gap-1 cursor-pointer min-h-[44px] sm:min-h-auto"
                onClick={onBack}
              >
                <div className="i-ph:arrow-left w-4 h-4" />
                <span>Use a different Email</span>
              </button>

              <button
                type="button"
                className={`text-[#888888] text-center sm:text-right min-h-[44px] sm:min-h-auto flex items-center justify-center sm:justify-end gap-1 ${
                  canResend ? 'cursor-pointer text-white hover:text-white' : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={canResend ? onResend : undefined}
                disabled={!canResend}
              >
                {IconsMapping.refresh}
                <span>Resend code {resendCountdown > 0 ? `in ${resendCountdown}s` : ''}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
